import type {
  ConfirmPlacementAction,
  DrawDraftOptionsAction,
  KingsCrownPlayer,
  KingsCrownState,
  NumberChip,
  PickDraftTokenAction,
  SyncViewerStateAction,
  TableChipSlot,
} from "../types";
import { KINGS_CROWN_MAX_HAND_SIZE } from "../types";
import {
  buildPlayerHandSlots,
  findBingo,
  getOtherPlayerId,
  getPlayer,
  hasAnyLegalPlacement,
  placeTokenOnBoard,
} from "../utils";

function rememberAction(
  state: KingsCrownState,
  actionId: string,
): readonly string[] {
  return state.completedActionIds.includes(actionId)
    ? state.completedActionIds
    : [...state.completedActionIds, actionId];
}

function addChipToPlayer(
  players: readonly KingsCrownPlayer[],
  playerId: string,
  chip: NumberChip,
): readonly KingsCrownPlayer[] {
  return players.map((player) => {
    if (player.playerId !== playerId) return player;
    const handSlots = buildPlayerHandSlots(player);
    const nextSlotIndex = handSlots.indexOf(null);

    return {
      ...player,
      hand: [...player.hand, chip],
      handSlots: handSlots.map((slot, index) =>
        index === nextSlotIndex ? chip : slot,
      ),
    };
  });
}

function removeChipFromPlayer(
  players: readonly KingsCrownPlayer[],
  playerId: string,
  chipId: string,
): readonly KingsCrownPlayer[] {
  return players.map((player) =>
    player.playerId === playerId
      ? {
          ...player,
          hand: player.hand.filter((chip) => chip.chipId !== chipId),
          handSlots: buildPlayerHandSlots(player).map((slot) =>
            slot?.chipId === chipId ? null : slot,
          ),
          remainingCrowns: player.remainingCrowns - 1,
        }
      : player,
  );
}

export function resolveBlockedPlacementTurn(
  state: KingsCrownState,
): KingsCrownState {
  if (state.phase !== "PLACE") return state;
  if (hasAnyLegalPlacement(state, state.currentPlayerId)) return state;

  return {
    ...state,
    phase: "ENDED",
    winnerId:
      state.lastPlacementPlayerId ??
      getOtherPlayerId(state, state.currentPlayerId),
    winLine: [],
  };
}

export function applyDrawDraftOptions(
  state: KingsCrownState,
  action: DrawDraftOptionsAction,
): KingsCrownState {
  const futureSlotIds = state.table
    .filter((slot) => slot.state === "future")
    .slice(0, 2)
    .map((slot) => slot.slotId);
  const chips = state.bag.slice(0, 2);

  if (futureSlotIds.length >= 2 && chips.length >= 2) {
    const nextTable = state.table.map((slot): TableChipSlot => {
      const chipIndex = futureSlotIds.indexOf(slot.slotId);
      const chip = chips[chipIndex];
      return chipIndex >= 0 && chip
        ? { ...slot, chip, face: "up", state: "occupied" }
        : slot;
    });

    return {
      ...state,
      phase: "DRAFT_PICK",
      bag: state.bag.slice(2),
      table: nextTable,
      drawnSlotIds: futureSlotIds,
      completedActionIds: rememberAction(state, action.actionId),
    };
  }

  const faceDownSlotIds = state.table
    .filter((slot) => slot.state === "occupied" && slot.face === "down")
    .slice(0, 2)
    .map((slot) => slot.slotId);
  if (faceDownSlotIds.length < 2) return state;
  const nextTable = state.table.map((slot): TableChipSlot => {
    return faceDownSlotIds.includes(slot.slotId)
      ? { ...slot, face: "up" }
      : slot;
  });

  return {
    ...state,
    phase: "DRAFT_PICK",
    table: nextTable,
    drawnSlotIds: faceDownSlotIds,
    completedActionIds: rememberAction(state, action.actionId),
  };
}

function closeDraftTurn(
  state: KingsCrownState,
  actionId: string,
): KingsCrownState {
  const handsComplete = state.players.every(
    (player) => player.hand.length >= KINGS_CROWN_MAX_HAND_SIZE,
  );
  if (handsComplete) {
    return resolveBlockedPlacementTurn({
      ...state,
      phase: "PLACE",
      drawnSlotIds: [],
      table: state.table.map((slot) =>
        slot.state === "occupied" ? { ...slot, face: "down" } : slot,
      ),
      completedActionIds: rememberAction(state, actionId),
    });
  }
  return {
    ...state,
    phase: "DRAFT_DRAW",
    currentPlayerId: getOtherPlayerId(state, state.currentPlayerId),
    drawnSlotIds: [],
    table: state.table.map((slot) =>
      slot.state === "occupied" ? { ...slot, face: "down" } : slot,
    ),
    completedActionIds: rememberAction(state, actionId),
  };
}

export function applyPickDraftToken(
  state: KingsCrownState,
  action: PickDraftTokenAction,
): KingsCrownState {
  if (action.source === "bag") {
    const chip = state.bag[0];
    if (!chip) return state;
    return closeDraftTurn(
      {
        ...state,
        bag: state.bag.slice(1),
        players: addChipToPlayer(state.players, action.playerId, chip),
      },
      action.actionId,
    );
  }

  const slot = state.table.find(
    (candidate) => candidate.slotId === action.slotId,
  );
  if (!slot?.chip) return state;

  return closeDraftTurn(
    {
      ...state,
      players: addChipToPlayer(state.players, action.playerId, slot.chip),
      table: state.table.map((candidate) =>
        candidate.slotId === slot.slotId
          ? { ...candidate, chip: null, face: null, state: "taken" }
          : candidate,
      ),
    },
    action.actionId,
  );
}

export function applyConfirmPlacement(
  state: KingsCrownState,
  action: ConfirmPlacementAction,
): KingsCrownState {
  const player = getPlayer(state, action.playerId);
  const chip = player?.hand.find(
    (candidate) => candidate.chipId === action.chipId,
  );
  if (!player || !chip) return state;

  const board = placeTokenOnBoard(state.board, action.cellId, {
    ownerId: action.playerId,
    chip,
  });
  const placedState: KingsCrownState = {
    ...state,
    board,
    players: removeChipFromPlayer(state.players, action.playerId, chip.chipId),
    lastPlacementPlayerId: action.playerId,
    completedActionIds: rememberAction(state, action.actionId),
  };
  const bingo = findBingo(placedState);
  if (bingo.winnerId) {
    return {
      ...placedState,
      phase: "ENDED",
      winnerId: bingo.winnerId,
      winLine: bingo.line,
    };
  }

  const nextPlayerId = getOtherPlayerId(placedState, action.playerId);
  return resolveBlockedPlacementTurn({
    ...placedState,
    currentPlayerId: nextPlayerId,
  });
}

export function applySyncViewerState(
  state: KingsCrownState,
  action: SyncViewerStateAction,
): KingsCrownState {
  return resolveBlockedPlacementTurn({
    ...state,
    completedActionIds: rememberAction(state, action.actionId),
  });
}
